import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, FilterBar, ErrorState, StatsCards } from '../../components/Common';
import type { FilterConfig, StatCard } from '../../components/Common';
import EmployeeDeductionForm from './EmployeeDeductionForm';
import { EmployeeDeduction, TableColumn } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import {
  useEmployeeDeductions,
  useCreateEmployeeDeduction,
  useUpdateEmployeeDeduction,
  useDeleteEmployeeDeduction,
} from '../../hooks/useEmployeeDeductions';
import { useEmployees } from '../../hooks/useEmployees';

const EmployeeDeductions: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingDeduction, setEditingDeduction] = useState<EmployeeDeduction | undefined>();

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'date', order: 'desc' as const }],
    pagination: { pageSize: 200 },
  }), []);
  // Employees have no `date` field, so they need their own sort
  const employeesQO = useMemo(() => ({
    sort: [{ field: 'name', order: 'asc' as const }],
    pagination: { pageSize: 500 },
  }), []);

  const { data: deductions, isLoading, error, refetch } = useEmployeeDeductions(queryOptions);
  const { data: employees } = useEmployees(employeesQO);

  const onSaved = () => {
    setShowForm(false);
    setEditingDeduction(undefined);
    setTimeout(() => refetch(), 100);
  };
  const createMutation = useCreateEmployeeDeduction(onSaved);
  const updateMutation = useUpdateEmployeeDeduction(onSaved);
  const deleteMutation = useDeleteEmployeeDeduction(() => setTimeout(() => refetch(), 100));

  const [searchTerm, setSearchTerm] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  const getEmployeeName = useCallback((d: any) => {
    if (d.employee && typeof d.employee === 'object') return d.employee.name || '-';
    const match = (employees || []).find(
      (e: any) => e.documentId === d.employeeId || String(e.id) === String(d.employeeId)
    );
    return match?.name || '-';
  }, [employees]);

  const filteredDeductions = useMemo(() => {
    if (!deductions) return [];
    return deductions.filter((d: any) => {
      const name = getEmployeeName(d);
      const matchesSearch = !searchTerm ||
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.reason?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesEmployee = employeeFilter === 'all' || String(d.employeeId) === String(employeeFilter);
      const date = new Date(d.date);
      const matchesFrom = !dateFromFilter || date >= new Date(dateFromFilter);
      const matchesTo = !dateToFilter || date <= new Date(dateToFilter);
      return matchesSearch && matchesEmployee && matchesFrom && matchesTo;
    });
  }, [deductions, searchTerm, employeeFilter, dateFromFilter, dateToFilter, getEmployeeName]);

  const totalAmount = useMemo(
    () => filteredDeductions.reduce((sum: number, d: any) => sum + (Number(d.amount) || 0), 0),
    [filteredDeductions]
  );

  const stats: StatCard[] = [
    { label: 'Deductions', value: filteredDeductions.length, color: 'blue' },
    { label: 'Total Amount', value: totalAmount.toLocaleString(), color: 'red' },
  ];

  const buildExportRow = (d: any) => ({
    'Employee': getEmployeeName(d),
    'Amount': d.amount,
    'Reason': d.reason || '-',
    'Date': d.date ? new Date(d.date).toLocaleDateString('en-GB') : '-',
  });

  const handleExport = () => exportToExcel(filteredDeductions.map(buildExportRow), `employee_deductions_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(filteredDeductions.map(buildExportRow), 'Employee Deductions');

  const filters: FilterConfig[] = [
    { key: 'employee', label: 'Employee', type: 'select', value: employeeFilter, onChange: setEmployeeFilter, options: [
      { value: 'all', label: 'All Employees' },
      ...(employees || []).map((e: any) => ({ value: e.documentId || String(e.id), label: e.name })),
    ]},
    { key: 'dateFrom', label: 'Date From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Date To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ];

  const handleClearFilters = () => {
    setSearchTerm('');
    setEmployeeFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
  };

  const columns: TableColumn[] = [
    { key: 'employeeId', label: 'Employee', sortable: false, render: (_: any, row: any) => getEmployeeName(row) },
    { key: 'amount', label: 'Amount', sortable: true, render: (value: number) => Number(value || 0).toLocaleString() },
    { key: 'reason', label: 'Reason', sortable: false },
    { key: 'date', label: 'Date', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString('en-GB') : '-' },
    { key: 'createdAt', label: 'Created', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString('en-GB') : '-' },
  ];

  const handleFormSubmit = async (data: Partial<EmployeeDeduction>) => {
    try {
      if (editingDeduction?.id) {
        await updateMutation.execute({ id: editingDeduction.id, data });
      } else {
        await createMutation.execute(data);
      }
    } catch (e) {
      console.error('Employee deduction save failed:', e);
    }
  };

  const handleDelete = async (deduction: EmployeeDeduction) => {
    if (!window.confirm('Are you sure you want to delete this deduction?')) return;
    try {
      await deleteMutation.execute(deduction);
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message="Error loading employee deductions." onRetry={refetch} entityName="employee deductions" />;

  if (showForm) {
    return (
      <div className="space-y-6">
        <EmployeeDeductionForm
          deduction={editingDeduction}
          onSubmit={handleFormSubmit}
          onCancel={() => { setShowForm(false); setEditingDeduction(undefined); }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Employee Deductions" description="Record amounts deducted from employees with reason and date" />
      <StatsCards stats={stats} columns={3} />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by employee or reason..."
        filters={filters}
        onClearFilters={handleClearFilters}
        totalCount={deductions?.length ?? 0}
        filteredCount={filteredDeductions.length}
        entityName="deductions"
        onAdd={() => { setEditingDeduction(undefined); setShowForm(true); }}
        addLabel="Add Deduction"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <Table
        columns={columns}
        data={filteredDeductions}
        onEdit={(d: any) => { setEditingDeduction(d); setShowForm(true); }}
        onDelete={handleDelete}
        title=""
        searchPlaceholder=""
        searchable={false}
      />
    </div>
  );
};

export default EmployeeDeductions;
